import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  ChartContainer,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { 
  TrendingUp, 
  Calendar, 
  CheckCircle2, 
  AlertTriangle,
  Scissors,
  ShoppingBag,
  Plus,
  TrendingDown,
  ArrowUpRight,
  ChevronDown,
  ChevronRight,
  CircleOff,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useLocation } from "wouter";
import type { Variedade, Ciclo as BaseCiclo, Mangara, Colheita } from "@shared/schema";
import { useSetores } from "@/hooks/use-setores";
import { useVariedades, useVariedadesDoCacho } from "@/hooks/use-variedades-cacho";
import { useToleranciaDias } from "@/hooks/use-tolerancia";
import { algumMangaraVencido } from "@shared/ciclo-fechamento";
import { hojeNaFazenda } from "@/lib/data-fazenda";

import { PageHeader } from "@/components/layout/page-header";
// Extended Ciclo type with harvest prediction dates
type Ciclo = BaseCiclo & {
  dataPrevisaoInicio: Date | null;
  dataPrevisaoFim: Date | null;
};

interface DashboardMetrics {
  previsaoColheita: number;
  cachosColhidos: number;
  percentualRealizado: number;
  ciclosAtrasados: number;
  mangarasMarcados: number;
  cachosParaColher: number;
  variacaoColheita: number | null;
  variacaoMangaras: number | null;
  proximasColheitas: Array<{
    id: string;
    setor: string;
    corFita: string;
    dataPrevista: string;
    quantidade: number;
  }>;
}

export default function Dashboard() {
  const [, setLocation] = useLocation();
  const [periodoFilter, setPeriodoFilter] = useState<string>("tudo");
  const [dataInicio, setDataInicio] = useState<string>("");
  const [dataFim, setDataFim] = useState<string>("");
  const [setorFilter, setSetorFilter] = useState<string>("todos");
  const [variedadeFilter, setVariedadeFilter] = useState<string>("todos");
  const [statusFilter, setStatusFilter] = useState<string>("ativos");

  const buildDashboardUrl = () => {
    const params = new URLSearchParams();
    params.append("periodo", periodoFilter);
    if (periodoFilter === "periodo-especifico" && dataInicio && dataFim) {
      params.append("dataInicio", dataInicio);
      params.append("dataFim", dataFim);
    }
    if (setorFilter !== "todos") params.append("setorId", setorFilter);
    if (variedadeFilter !== "todos") params.append("variedadeId", variedadeFilter);
    if (statusFilter !== "todos") params.append("status", statusFilter);
    return `/api/dashboard?${params.toString()}`;
  };

  const { data: metrics, isLoading: metricsLoading} = useQuery<DashboardMetrics>({
    queryKey: [buildDashboardUrl()],
  });

  const { data: ciclos, isLoading: ciclosLoading } = useQuery<Ciclo[]>({
    queryKey: ["/api/ciclos"],
  });

  // Lista completa para lookups históricos; produtivos para filtros de seleção
  const { setores } = useSetores();
  const { setores: setoresProdutivos } = useSetores({ somenteProdutivos: true });

  // Duas listas, de propósito (mesmo par de `useSetores` em ciclos.tsx):
  // `variedades` é a lista COMPLETA, para o LOOKUP do nome da variedade de cada
  // setor — filtrar ali faria um setor de citros aparecer sem rótulo.
  // `variedadesDoCacho` é a do SELETOR, que é onde Pêra Rio não pode estar.
  const { data: variedades } = useVariedades();
  const { variedades: variedadesDoCacho } = useVariedadesDoCacho();
  const toleranciaDias = useToleranciaDias();

  const { data: mangaras } = useQuery<Mangara[]>({
    queryKey: ["/api/mangaras"],
  });

  const { data: colheitas } = useQuery<Colheita[]>({
    queryKey: ["/api/colheitas"],
  });

  const { data: coresFitas } = useQuery<any[]>({
    queryKey: ["/api/cores"],
  });

  const buildGraphUrl = (endpoint: string) => {
    const params = new URLSearchParams();
    params.append("periodo", periodoFilter);
    if (periodoFilter === "periodo-especifico" && dataInicio && dataFim) {
      params.append("dataInicio", dataInicio);
      params.append("dataFim", dataFim);
    }
    if (setorFilter !== "todos") params.append("setorId", setorFilter);
    if (variedadeFilter !== "todos") params.append("variedadeId", variedadeFilter);
    return `/api/dashboard/${endpoint}?${params.toString()}`;
  };

  const { data: emissaoCachosData, isLoading: emissaoCachosLoading } = useQuery<any[]>({
    queryKey: [buildGraphUrl("emissao-cachos")],
  });

  const { data: colheitasPorMesData, isLoading: colheitasPorMesLoading } = useQuery<any[]>({
    queryKey: [buildGraphUrl("colheitas-por-mes")],
  });

  const { data: projecaoData } = useQuery<{ colhidoMesAtual: number; meses: Array<{ ano: number; mes: number; saldo: number }> }>({
    queryKey: [buildGraphUrl("projecao-colheitas")],
  });

  // Merge da projeção nas linhas do gráfico Colheitas por Mês. Para cada bucket
  // {ano,mes,saldo}, grava a chave proj_${ano} na linha do mês. Mês atual emenda
  // na sólida (colhidoMesAtual + saldo); meses futuros recebem só o saldo.
  const colheitasComProjecao = useMemo(() => {
    const base = (colheitasPorMesData || []).map((r: any) => ({ ...r }));
    if (projecaoData?.meses?.length) {
      const hoje = new Date();
      const curYear = hoje.getFullYear();
      const curMonth = hoje.getMonth() + 1;
      const byMes = new Map<string, any>(base.map((r: any) => [r.mes, r]));

      for (const { ano, mes, saldo } of projecaoData.meses) {
        const mesPad = String(mes).padStart(2, "0");
        let row = byMes.get(mesPad);
        if (!row) {
          row = { mes: mesPad, label: new Date(2024, mes - 1).toLocaleDateString("pt-BR", { month: "short" }) };
          byMes.set(mesPad, row);
          base.push(row);
        }
        const isCurrent = ano === curYear && mes === curMonth;
        row[`proj_${ano}`] = isCurrent ? (projecaoData.colhidoMesAtual + saldo) : saldo;
      }

      base.sort((a: any, b: any) => String(a.mes).localeCompare(String(b.mes)));
    }
    return base;
  }, [colheitasPorMesData, projecaoData]);

  // Séries do gráfico: anos sólidos (cores da paleta por ordem) e anos de
  // projeção (mesma cor do ano, translúcida; ano novo pega a próxima cor livre).
  const chartSeries = useMemo(() => {
    const palette = ["#3b82f6", "#ef4444", "#10b981", "#f59e0b", "#8b5cf6"];
    const keys = colheitasComProjecao.flatMap((d: any) => Object.keys(d));
    const solidYears = Array.from(new Set(
      keys.filter((k: string) => k !== "mes" && k !== "label" && !k.startsWith("proj_") && !isNaN(Number(k)))
    )).sort();
    const yearColor: Record<string, string> = {};
    solidYears.forEach((y, i) => { yearColor[y] = palette[i % palette.length]; });

    const projYears = Array.from(new Set(
      keys.filter((k: string) => k.startsWith("proj_")).map((k: string) => k.slice(5))
    )).sort();
    let nextIdx = solidYears.length;
    projYears.forEach((y) => {
      if (!yearColor[y]) { yearColor[y] = palette[nextIdx % palette.length]; nextIdx += 1; }
    });

    return { solidYears, projYears, yearColor };
  }, [colheitasComProjecao]);

  const buildSetoresUrl = () => {
    const params = new URLSearchParams();
    params.append("periodo", periodoFilter);
    if (periodoFilter === "periodo-especifico" && dataInicio && dataFim) {
      params.append("dataInicio", dataInicio);
      params.append("dataFim", dataFim);
    }
    if (setorFilter !== "todos") params.append("setorId", setorFilter);
    if (variedadeFilter !== "todos") params.append("variedadeId", variedadeFilter);
    if (statusFilter !== "todos") params.append("status", statusFilter);
    return `/api/dashboard/setores?${params.toString()}`;
  };

  const { data: setoresData, isLoading: setoresLoading } = useQuery<any[]>({
    queryKey: [buildSetoresUrl()],
  });

  const ciclosAtivos = ciclos?.filter(c => c.status === "ATIVO") || [];

  // Calcular total de colheitas "Sem Fita"
  const colheitasSemFita = useMemo(() => {
    if (!colheitas) return { count: 0, total: 0 };
    
    const semFita = colheitas.filter(c => (c as any).semFita === true);
    return {
      count: semFita.length,
      total: semFita.reduce((sum, c) => sum + (c.quantidadeCachos || 0), 0)
    };
  }, [colheitas]);

  // Calcular performance por setor com filtros aplicados
  const performancePorSetor = useMemo(() => {
    if (!setores || !mangaras || !colheitas || !ciclos || !coresFitas) return [];

    // Parse date filters
    const today = new Date();
    let startDate: Date | null = null;
    let endDate: Date | null = null;
    
    if (periodoFilter === "mes") {
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    } else if (periodoFilter === "trimestre") {
      startDate = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
      endDate = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3 + 3, 0);
    } else if (periodoFilter === "semestre") {
      startDate = new Date(today.getFullYear(), Math.floor(today.getMonth() / 6) * 6, 1);
      endDate = new Date(today.getFullYear(), Math.floor(today.getMonth() / 6) * 6 + 6, 0);
    } else if (periodoFilter === "ano") {
      startDate = new Date(today.getFullYear(), 0, 1);
      endDate = new Date(today.getFullYear(), 11, 31);
    } else if (periodoFilter === "periodo-especifico" && dataInicio && dataFim) {
      startDate = new Date(dataInicio);
      endDate = new Date(dataFim);
    }

    return setores
      .filter(setor => {
        // Apply setor filter
        if (setorFilter !== "todos" && setor.id !== setorFilter) return false;
        // Apply variedade filter
        if (variedadeFilter !== "todos" && setor.variedadeId !== variedadeFilter) return false;
        return setor.ativo;
      })
      .map(setor => {
        // Filtrar mangarás e colheitas deste setor
        let mangarasSetor = mangaras.filter(m => m.setorId === setor.id) || [];
        let colheitasSetor = colheitas.filter(c => c.setorId === setor.id) || [];
        
        // Apply period filter to mangarás
        if (startDate && endDate) {
          mangarasSetor = mangarasSetor.filter(m => {
            if (!m.dataPrevistaColheita) return false;
            const prevista = new Date(m.dataPrevistaColheita);
            return prevista >= startDate && prevista <= endDate;
          });
        }

        // Apply period filter to colheitas
        if (startDate && endDate) {
          colheitasSetor = colheitasSetor.filter(c => {
            const colheita = new Date(c.dataColheita);
            return colheita >= startDate && colheita <= endDate;
          });
        }
        
        const totalMarcado = mangarasSetor.reduce((sum, m) => sum + (m.quantidadeCachos || 0), 0);
        const totalColhido = colheitasSetor.reduce((sum, c) => sum + (c.quantidadeCachos || 0), 0);
        
        const percentualColheita = totalMarcado > 0 ? (totalColhido / totalMarcado) * 100 : 0;
        
        // Performance esperada: cachos por muda (esperado: ~1 cacho por muda ao ano)
        const cachoPorMuda = setor.quantidadeMudas > 0 ? totalMarcado / setor.quantidadeMudas : 0;
        
        // Performance por fita dentro do setor
        const fitasPorSetor = ciclos?.filter(ciclo => {
          const mangarasCiclo = mangarasSetor.filter(m => m.cicloId === ciclo.id);
          return mangarasCiclo.length > 0;
        }).map(ciclo => {
          const mangarasCiclo = mangarasSetor.filter(m => m.cicloId === ciclo.id);
          const colheitasCiclo = colheitasSetor.filter(c => c.cicloId === ciclo.id);
          
          const totalCachosCiclo = mangarasCiclo.reduce((sum, m) => sum + (m.quantidadeCachos || 0), 0);
          const totalColhidoCiclo = colheitasCiclo.reduce((sum, c) => sum + (c.quantidadeCachos || 0), 0);
          const percentualCiclo = totalCachosCiclo > 0 ? (totalColhidoCiclo / totalCachosCiclo) * 100 : 0;
          
          const corFita = coresFitas?.find(c => c.id === ciclo.corFitaId);
          
          return {
            codigo: ciclo.codigo,
            corNome: corFita?.nome || '',
            hexColor: corFita?.hexColor || '#000',
            totalCachos: totalCachosCiclo,
            totalColhido: totalColhidoCiclo,
            percentualColheita: percentualCiclo,
          };
        }).filter(f => f.totalCachos > 0) || [];

        const variedade = variedades?.find(v => v.id === setor.variedadeId);
        
        return {
          ...setor,
          variedade,
          totalMarcado,
          totalColhido,
          percentualColheita,
          cachoPorMuda,
          fitasPorSetor,
        };
      }).sort((a, b) => b.percentualColheita - a.percentualColheita);
  }, [setores, mangaras, colheitas, ciclos, coresFitas, variedades, setorFilter, variedadeFilter, periodoFilter, dataInicio, dataFim]);

  // Verificar se um ciclo está realmente atrasado (previsão passou há mais de 15 dias)
  const isCicloAtrasado = (ciclo: Ciclo): boolean => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    
    const cicloMangaras = mangaras?.filter(m => m.cicloId === ciclo.id) || [];

    // Algum mangará passou da previsão + tolerância CONFIGURADA (era `+ 15`
    // cravado, com a fazenda em 35 — atrasava o rótulo em 20 dias).
    return algumMangaraVencido(
      cicloMangaras.map(m => m.dataPrevistaColheita),
      toleranciaDias,
      hojeNaFazenda(),
    );
  };

  const getCicloStatus = (ciclo: Ciclo): { label: string; variant: "default" | "secondary" | "destructive"; className?: string } => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    // Verificar se está atrasado (passou 15 dias da previsão)
    if (isCicloAtrasado(ciclo)) {
      return { label: "Atrasado", variant: "destructive" };
    }

    // Em Andamento: hoje está DENTRO da janela de previsão do ciclo.
    //
    // 🔴 `dataPrevisaoInicio/Fim` JÁ VÊM do servidor com a tolerância somada
    // (getAllCiclos: MIN(previsto) − tolerância, MAX(previsto) + tolerância).
    // Somar mais 15 dias aqui contava a tolerância DUAS VEZES e esticava o
    // "Em Andamento" em meio mês. `inicio.tsx` sempre fez certo — esta tela é
    // que estava fora do padrão.
    if (ciclo.dataPrevisaoInicio && ciclo.dataPrevisaoFim) {
      const inicio = new Date(ciclo.dataPrevisaoInicio);
      const fim = new Date(ciclo.dataPrevisaoFim);
      inicio.setHours(0, 0, 0, 0);
      fim.setHours(0, 0, 0, 0);

      if (hoje >= inicio && hoje <= fim) {
        return { 
          label: "Em Andamento", 
          variant: "default",
          className: "bg-sucesso-suave text-sucesso border-sucesso/30"
        };
      }
    }

    // Caso contrário → Próximo (ainda não começou)
    return { label: "Próximo", variant: "secondary" };
  };

  // Formata a janela de colheita do ciclo (dataPrevisaoInicio → dataPrevisaoFim).
  // Sem somar tolerância: as duas datas já vêm do servidor com ela embutida —
  // o `+ 15` daqui mostrava uma janela meio mês mais longa do que a real.
  const formatarJanelaColheita = (ciclo: Ciclo): string => {
    if (!ciclo.dataPrevisaoInicio || !ciclo.dataPrevisaoFim) {
      return ciclo.mesAno; // Fallback para mesAno se não houver datas
    }

    const mesesAbrev = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

    // Parse dates using UTC to avoid timezone issues
    const inicio = new Date(ciclo.dataPrevisaoInicio + 'T00:00:00Z');
    const fim = new Date(ciclo.dataPrevisaoFim + 'T00:00:00Z');

    const mesInicio = mesesAbrev[inicio.getUTCMonth()];
    const mesFim = mesesAbrev[fim.getUTCMonth()];
    const anoFim = fim.getUTCFullYear();
    
    // Se os meses são iguais, mostrar apenas um
    if (mesInicio === mesFim) {
      return `${mesInicio}/${anoFim}`;
    }
    
    // Se meses diferentes, mostrar o intervalo
    return `${mesInicio}-${mesFim}/${anoFim}`;
  };

  // Função para ordenar ciclos por mês de colheita previsto
  const sortCiclosByMesAno = (ciclos: Ciclo[]) => {
    return [...ciclos].sort((a, b) => {
      // Converter mesAno para formato comparável
      const meses: Record<string, number> = {
        jan: 1, fev: 2, mar: 3, abr: 4, mai: 5, jun: 6,
        jul: 7, ago: 8, set: 9, out: 10, nov: 11, dez: 12
      };
      
      // Remove separadores (/, -, _) se existirem e extrai mês e ano
      const parsesMesAno = (mesAno: string) => {
        // Remove qualquer caractere não alfanumérico
        const normalized = mesAno.replace(/[^a-zA-Z0-9]/g, '');
        const mes = normalized.substring(0, 3).toLowerCase();
        const ano = parseInt(normalized.substring(3)) || 0;
        return { mes, ano };
      };
      
      const parsedA = parsesMesAno(a.mesAno);
      const parsedB = parsesMesAno(b.mesAno);
      
      // Ordenar por ano primeiro, depois por mês
      if (parsedA.ano !== parsedB.ano) {
        return parsedA.ano - parsedB.ano;
      }
      return (meses[parsedA.mes] || 0) - (meses[parsedB.mes] || 0);
    });
  };

  // Helper para extrair informações da fita para exibição
  const getFitaInfo = (ciclo: Ciclo) => {
    const mangarasCiclo = mangaras?.filter(m => m.cicloId === ciclo.id) || [];
    const firstMangara = mangarasCiclo.sort((a, b) => 
      (a.semanaCorteMangara || 0) - (b.semanaCorteMangara || 0)
    )[0];
    
    const corFita = coresFitas?.find(c => c.id === ciclo.corFitaId);
    
    return {
      corNome: corFita?.nome || 'N/A',
      hexColor: corFita?.hexColor || '#000',
      mangaraWeek: firstMangara?.semanaCorteMangara || null,
      harvestWeek: firstMangara?.semanaPrevistaColheita || null,
      harvestDate: firstMangara?.dataPrevistaColheita || null,
    };
  };

  const MetricCard = ({ 
    title, 
    value, 
    icon: Icon, 
    trend, 
    trendValue,
    subtitle 
  }: { 
    title: string; 
    value: string | number; 
    icon: any; 
    trend?: "up" | "down"; 
    trendValue?: string;
    subtitle?: string;
  }) => (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0 pb-1 p-3 md:p-4">
        <CardTitle className="text-xs md:text-sm font-medium text-muted-foreground leading-tight">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
      </CardHeader>
      <CardContent className="p-3 md:p-4 pt-0">
        <div className="text-xl md:text-2xl font-bold" data-testid={`metric-${title.toLowerCase().replace(/\s+/g, "-")}`}>
          {value}
        </div>
        {subtitle && (
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        )}
      </CardContent>
    </Card>
  );

  if (metricsLoading) {
    return (
      <div className="space-y-8">
        <PageHeader titulo="Dashboard" descricao="Visão geral da produção" />

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-32" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-9 w-20" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-hidden w-full">
      <div className="space-y-4 md:space-y-8 px-0">
        <PageHeader titulo="Dashboard" descricao="Visão geral da produção" />

        {/* Filters */}
        <div className="grid gap-3 md:gap-4 grid-cols-2 md:grid-cols-4">
        <div className="space-y-1.5">
          <label className="text-xs md:text-sm font-medium">Período</label>
          <Select value={periodoFilter} onValueChange={setPeriodoFilter}>
            <SelectTrigger data-testid="filter-periodo">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tudo">Tudo</SelectItem>
              <SelectItem value="mes">Mês Atual</SelectItem>
              <SelectItem value="mes-passado">Mês Passado</SelectItem>
              <SelectItem value="proximo-mes">Próximo Mês</SelectItem>
              <SelectItem value="trimestre">Trimestre</SelectItem>
              <SelectItem value="semestre">Semestre</SelectItem>
              <SelectItem value="ano">Ano</SelectItem>
              <SelectItem value="periodo-especifico">Período Específico</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs md:text-sm font-medium">Setor</label>
          <Select value={setorFilter} onValueChange={setSetorFilter}>
            <SelectTrigger data-testid="filter-setor">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os Setores</SelectItem>
              {setoresProdutivos?.filter(s => s.ativo).sort((a, b) => a.nome.localeCompare(b.nome)).map((setor) => (
                <SelectItem key={setor.id} value={setor.id}>{setor.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs md:text-sm font-medium">Variedade</label>
          <Select value={variedadeFilter} onValueChange={setVariedadeFilter}>
            <SelectTrigger data-testid="filter-variedade">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todas as Variedades</SelectItem>
              {variedadesDoCacho?.filter(v => v.ativo).map((variedade) => (
                <SelectItem key={variedade.id} value={variedade.id}>{variedade.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs md:text-sm font-medium">Status</label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ativos">Ciclos Ativos</SelectItem>
              <SelectItem value="todos">Todos os Status</SelectItem>
              <SelectItem value="atrasado">Atrasado</SelectItem>
              <SelectItem value="no-prazo">No Prazo</SelectItem>
              <SelectItem value="proximo">Próximo</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Campos de data específica quando Período Específico é selecionado */}
      {periodoFilter === "periodo-especifico" && (
        <div className="grid gap-3 md:gap-4 grid-cols-2">
          <div className="space-y-2">
            <label className="text-xs md:text-sm font-medium">Data Início</label>
            <input
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-xs md:text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              data-testid="input-data-inicio"
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs md:text-sm font-medium">Data Fim</label>
            <input
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-xs md:text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              data-testid="input-data-fim"
            />
          </div>
        </div>
      )}

      {/* Metrics Grid */}
      <div className="grid gap-3 md:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Mangarás Marcados"
          value={metrics?.mangarasMarcados || 0}
          icon={Scissors}
          subtitle="cachos"
        />
        <MetricCard
          title="Cachos Colhidos"
          value={metrics?.cachosColhidos || 0}
          icon={CheckCircle2}
          subtitle={`${metrics?.percentualRealizado || 0}% realizado`}
        />
        <MetricCard
          title="Cachos para Colher"
          value={metrics?.cachosParaColher || 0}
          icon={Calendar}
          subtitle="cachos"
        />
        <MetricCard
          title="Fitas em Atraso"
          value={metrics?.ciclosAtrasados || 0}
          icon={AlertTriangle}
          subtitle="fitas"
        />
      </div>

      {/* Card "Sem Fita" destacado */}
      {colheitasSemFita.total > 0 && (
        <Card className="border-atencao/50 bg-atencao-suave/50">
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2 p-3 md:p-4">
            <div className="flex items-center gap-2">
              <CircleOff className="h-5 w-5 text-atencao" />
              <CardTitle className="text-sm md:text-base font-semibold text-atencao">
                Colheitas Sem Fita
              </CardTitle>
            </div>
            <Badge 
              variant="outline" 
              className="border-atencao/30 bg-atencao-suave text-atencao"
            >
              {colheitasSemFita.count} {colheitasSemFita.count === 1 ? 'registro' : 'registros'}
            </Badge>
          </CardHeader>
          <CardContent className="p-3 md:p-4 pt-0">
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-atencao" data-testid="metric-colheitas-sem-fita">
                {colheitasSemFita.total.toLocaleString('pt-BR')}
              </span>
              <span className="text-sm text-atencao/80">cachos</span>
            </div>
            <p className="text-xs text-atencao/80 mt-1">
              Colheitas registradas sem identificação de fita
            </p>
          </CardContent>
        </Card>
      )}

      {/* Emissão de Cachos Chart - Comparação por Ano */}
      <Card>
        <CardHeader className="p-4 md:p-6">
          <div className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg md:text-2xl font-semibold">Emissão de Cachos</CardTitle>
          </div>
          <p className="text-xs md:text-sm text-muted-foreground">Comparação de mangarás mensais por ano</p>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0">
          {emissaoCachosLoading ? (
            <Skeleton className="h-96 md:h-[500px] w-full" />
          ) : !emissaoCachosData || emissaoCachosData.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-12">
              Nenhum dado de emissão disponível
            </p>
          ) : (
            <div className="-mx-2 md:mx-0">
              <ChartContainer
                config={{
                  2024: { label: "2024", color: "#3b82f6" },
                  2025: { label: "2025", color: "#ef4444" },
                  2026: { label: "2026", color: "#10b981" },
                  2027: { label: "2027", color: "#f59e0b" },
                  2028: { label: "2028", color: "#8b5cf6" },
                }}
                className="h-64 md:h-[500px] w-full"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={emissaoCachosData} margin={{ right: 10, bottom: 20, left: 10, top: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="label" 
                      tick={{ fontSize: 10 }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis tick={{ fontSize: 10 }} width={45} />
                    <Tooltip content={<ChartTooltipContent />} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                  {emissaoCachosData && emissaoCachosData.length > 0 ? (
                    Array.from(new Set(
                      emissaoCachosData.flatMap(d => Object.keys(d))
                        .filter(key => key !== 'mes' && key !== 'label' && !isNaN(Number(key)))
                    )).sort()
                      .map((year, idx) => {
                        const colors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6'];
                        return (
                          <Line
                            key={year}
                            type="monotone"
                            dataKey={year}
                            stroke={colors[idx % colors.length]}
                            strokeWidth={2}
                            dot={{ r: 4 }}
                            connectNulls
                          />
                        );
                      })
                  ) : null}
                </LineChart>
              </ResponsiveContainer>
              </ChartContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Colheitas por Mês Chart - Comparação por Ano */}
      <Card>
        <CardHeader className="p-4 md:p-6">
          <div className="flex items-center gap-2">
            <Scissors className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg md:text-2xl font-semibold">Colheitas por Mês</CardTitle>
          </div>
          <p className="text-xs md:text-sm text-muted-foreground">Comparação de colheitas mensais por ano</p>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0">
          {colheitasPorMesLoading ? (
            <Skeleton className="h-96 md:h-[500px] w-full" />
          ) : !colheitasComProjecao || colheitasComProjecao.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-12">
              Nenhum dado de colheita disponível
            </p>
          ) : (
            <div className="-mx-2 md:mx-0">
              <ChartContainer
                config={{
                  2024: { label: "2024", color: "#3b82f6" },
                  2025: { label: "2025", color: "#ef4444" },
                  2026: { label: "2026", color: "#10b981" },
                  2027: { label: "2027", color: "#f59e0b" },
                  2028: { label: "2028", color: "#8b5cf6" },
                }}
                className="h-64 md:h-[500px] w-full"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={colheitasComProjecao} margin={{ right: 10, bottom: 20, left: 10, top: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 10 }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis tick={{ fontSize: 10 }} width={45} />
                    <Tooltip content={<ChartTooltipContent />} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                  {chartSeries.solidYears.map((year) => (
                    <Line
                      key={year}
                      type="monotone"
                      dataKey={year}
                      stroke={chartSeries.yearColor[year]}
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      connectNulls
                    />
                  ))}
                  {chartSeries.projYears.map((year) => (
                    <Line
                      key={`proj_${year}`}
                      type="monotone"
                      dataKey={`proj_${year}`}
                      stroke={chartSeries.yearColor[year]}
                      strokeOpacity={0.45}
                      strokeDasharray="6 4"
                      strokeWidth={2}
                      dot={{ r: 2 }}
                      name={`Projeção ${year}`}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
              </ChartContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Performance por Setor */}
      <Card data-testid="card-performance-setor">
        <CardHeader className="p-4 md:p-6">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg md:text-2xl font-semibold">Performance por Setor</CardTitle>
          </div>
          <p className="text-xs md:text-sm text-muted-foreground">Desempenho de colheita e produtividade por área</p>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0">
          {metricsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          ) : performancePorSetor.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Nenhum setor cadastrado
            </p>
          ) : (
            <div className="space-y-3">
              {performancePorSetor.map((setor) => (
                <SetorPerformanceCard key={setor.id} setor={setor} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  );
}

// Componente para card de performance do setor (com collapsible)
function SetorPerformanceCard({ setor }: { setor: any }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card data-testid={`setor-performance-${setor.id}`}>
        <CollapsibleTrigger asChild>
          <CardContent className="p-3 md:p-4 cursor-pointer hover-elevate">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-sm md:text-base">{setor.nome}</h3>
                  {setor.variedade && <Badge variant="outline" className="text-xs">{setor.variedade.nome}</Badge>}
                  {setor.fitasPorSetor.length > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {setor.fitasPorSetor.length} {setor.fitasPorSetor.length === 1 ? 'fita' : 'fitas'}
                    </Badge>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                  <div>
                    <p className="text-muted-foreground">Mudas</p>
                    <p className="font-semibold">{setor.quantidadeMudas.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Marcado</p>
                    <p className="font-semibold">{setor.totalMarcado.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Colhido</p>
                    <p className="font-semibold text-sucesso">
                      {setor.totalColhido.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">% Colheita</p>
                    <p className="font-semibold" data-testid={`setor-percentual-${setor.id}`}>
                      {setor.percentualColheita.toFixed(1)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Cachos/Muda</p>
                    <p className="font-semibold flex items-center gap-1">
                      {setor.cachoPorMuda.toFixed(2)}
                      {setor.cachoPorMuda >= 0.8 ? (
                        <TrendingUp className="h-3 w-3 text-sucesso" />
                      ) : (
                        <TrendingDown className="h-3 w-3 text-atencao" />
                      )}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="hidden md:block w-32">
                  <Progress value={setor.percentualColheita} className="h-2" />
                </div>
                {setor.fitasPorSetor.length > 0 && (
                  isOpen ? (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  )
                )}
              </div>
            </div>
          </CardContent>
        </CollapsibleTrigger>
        
        {setor.fitasPorSetor.length > 0 && (
          <CollapsibleContent>
            <CardContent className="pt-0 pb-3 md:pb-4 px-3 md:px-4">
              <div className="border-t pt-3 space-y-2">
                <p className="text-xs text-muted-foreground font-medium mb-2">Detalhamento por Fita:</p>
                {setor.fitasPorSetor.map((fita: any, idx: number) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-2 rounded-md bg-muted/30"
                    data-testid={`setor-fita-${setor.id}-${idx}`}
                  >
                    <div className="flex items-center gap-2 flex-1">
                      <div 
                        className="w-3 h-3 rounded-sm flex-shrink-0" 
                        style={{ backgroundColor: fita.hexColor }}
                      />
                      <span className="text-xs font-mono font-medium">{fita.codigo}</span>
                      <span className="text-xs text-muted-foreground">({fita.corNome})</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <div className="text-right">
                        <span className="text-muted-foreground">Marcado: </span>
                        <span className="font-semibold">{fita.totalCachos}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-muted-foreground">Colhido: </span>
                        <span className="font-semibold text-sucesso">
                          {fita.totalColhido}
                        </span>
                      </div>
                      <div className="text-right min-w-[60px]">
                        <span className="font-semibold">{fita.percentualColheita.toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </CollapsibleContent>
        )}
      </Card>
    </Collapsible>
  );
}
